import { useState } from "react";
import {
  Menu,
  Button,
  MenuItem,
  Typography,
  ListItemIcon,
  ListItemText,
  Divider,
  Tooltip,
} from "@mui/material";
import Iconify from "../../../components/iconify";
import { Icon } from "@iconify/react";
import { useSelector } from "react-redux";
import { selectCurrentUserData } from "../../../features/auth/authSlice";
import { useUpdateMemberTableSettingsMutation } from "../../../features/users/usersApiSlice";

const SORT_BY_OPTIONS = [
  { value: "createdAt", sort: "desc", label: "Erstellt: Neu zuerst" },
  { value: "createdAt", sort: "asc", label: "Erstellt: Alt zuerst" },
  { value: "lastName", sort: "asc", label: "Name: A-Z" },
  { value: "lastName", sort: "desc", label: "Name: Z-A" },
  { value: "number", sort: "asc", label: "Mitglieds-Nr.: Niedrige zuerst" },
  { value: "number", sort: "desc", label: "Mitglieds-Nr.: Höchste zuerst" },
  { value: "birthdate", sort: "asc", label: "Geburtstag: Niedrige zuerst" },
  { value: "birthdate", sort: "desc", label: "Geburtstag: Höchste zuerst" },
  { value: "age", sort: "asc", label: "Alter: Jüngste zuerst" },
  { value: "age", sort: "desc", label: "Alter: Älteste zuerst" },
  { value: "degree", sort: "asc", label: "Grad: Niedrige zuerst" },
  { value: "degree", sort: "desc", label: "Grad: Höchste zuerst" },
  { value: "lastTesting", sort: "asc", label: "Prüfung: Letzte zuerst" },
  { value: "lastTesting", sort: "desc", label: "Prüfung: Neueste zuerst" },
];

export { SORT_BY_OPTIONS };

export default function MemberSort({ onSort }) {
  const [open, setOpen] = useState(null);
  const [sort, setSort] = useState("");

  const userData = useSelector(selectCurrentUserData);
  const [updateMemberTableSettings] = useUpdateMemberTableSettingsMutation();

  const savedDefault = userData?.settings?.memberTableSettings?.defaultSort;

  const handleOpen = (event) => {
    setOpen(event.currentTarget);
  };

  const handleClose = () => {
    setOpen(null);
  };

  const handleSelectSort = (option) => {
    setSort(option.label);
    onSort(option.value, option.sort);
    handleClose();
  };

  const handleSetDefault = async (option, e) => {
    e.stopPropagation();
    try {
      const existingColumns =
        userData?.settings?.memberTableSettings?.defaultColumns || [];
      await updateMemberTableSettings({
        memberTableSettings: {
          defaultColumns: existingColumns,
          defaultSort: { value: option.value, order: option.sort },
        },
      }).unwrap();
      setSort(option.label);
      onSort(option.value, option.sort);
    } catch (err) {
      console.error("Fehler beim Speichern der Sortierung:", err);
    }
    handleClose();
  };

  const isDefault = (option) =>
    savedDefault?.value === option.value && savedDefault?.order === option.sort;

  return (
    <>
      <Button
        sx={{ mr: 2 }}
        disableRipple
        color="secondary"
        variant="contained"
        onClick={handleOpen}
        endIcon={
          <Iconify
            icon={open ? "eva:chevron-up-fill" : "eva:chevron-down-fill"}
          />
        }
      >
        Sortieren:&nbsp;
        <Typography
          component="span"
          variant="subtitle2"
          sx={{ color: "text.secondary" }}
        >
          {sort}
        </Typography>
      </Button>
      <Menu
        keepMounted
        anchorEl={open}
        open={Boolean(open)}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          paper: { sx: { minWidth: 280, maxHeight: 400 } },
        }}
      >
        {SORT_BY_OPTIONS.map((option) => (
          <MenuItem
            key={option.label}
            selected={option.label === sort}
            onClick={() => handleSelectSort(option)}
            sx={{ typography: "body2", pr: 1 }}
          >
            <ListItemText>
              {option.label}
              {isDefault(option) && (
                <Typography
                  component="span"
                  variant="caption"
                  sx={{
                    ml: 1,
                    px: 0.8,
                    py: 0.2,
                    borderRadius: 1,
                    background:
                      "linear-gradient(135deg, #7c3aed 0%, #6366f1 100%)",
                    color: "#fff",
                    fontSize: "0.65rem",
                    fontWeight: 700,
                  }}
                >
                  Standard
                </Typography>
              )}
            </ListItemText>
            <Tooltip title="Als Standard speichern" arrow>
              <ListItemIcon
                sx={{ minWidth: "auto", ml: 1 }}
                onClick={(e) => handleSetDefault(option, e)}
              >
                <Icon
                  icon={
                    isDefault(option)
                      ? "mdi:star"
                      : "mdi:star-outline"
                  }
                  width={18}
                  style={{
                    color: isDefault(option) ? "#7c3aed" : "#999",
                  }}
                />
              </ListItemIcon>
            </Tooltip>
          </MenuItem>
        ))}
        {savedDefault?.value && (
          <>
            <Divider />
            <MenuItem
              onClick={async () => {
                try {
                  const existingColumns =
                    userData?.settings?.memberTableSettings?.defaultColumns ||
                    [];
                  await updateMemberTableSettings({
                    memberTableSettings: {
                      defaultColumns: existingColumns,
                      defaultSort: { value: "", order: "" },
                    },
                  }).unwrap();
                } catch (err) {
                  console.error("Fehler beim Entfernen der Sortierung:", err);
                }
                handleClose();
              }}
              sx={{ typography: "body2", color: "text.secondary" }}
            >
              <ListItemIcon sx={{ minWidth: 32 }}>
                <Icon icon="mdi:star-off" width={18} />
              </ListItemIcon>
              Standard entfernen
            </MenuItem>
          </>
        )}
      </Menu>
    </>
  );
}
